import { makeAutoObservable, runInAction } from 'mobx'
import axios from 'axios'

import { User } from '@/api/authApi'
import { normalizeEmail } from '@/lib/utils'

class UserStore {
  user: User | null = null
  isLoading: boolean = false
  error: string | null = null
  /** Machine-readable reason sent with a refused sign-in, when the server has one. */
  errorCode: string | null = null
  isAuthenticated: boolean = false

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
    // Initialize the store based on token presence
    this.checkAuth()
  }
  get isLoggedIn() {
    return !!this.user
  }

  get isAdmin() {
    if (!this.user?.email) return false
    // Both sides normalized: allowlist entries are operator-typed and the stored address
    // is whatever an admin typed when creating the account. Mirrors isAdminEmail on the server.
    const adminUsers = (import.meta.env.VITE_ADMIN_USERS || '').split(',').map((e: string) => normalizeEmail(e))
    return adminUsers.includes(normalizeEmail(this.user.email))
  }

  get isCoach() {
    return this.user?.is_coach === true
  }

  setUser(user: User | null) {
    this.user = user
    this.isAuthenticated = !!user
  }

  setLoading(loading: boolean) {
    this.isLoading = loading
  }

  setError(error: string | null, code: string | null = null) {
    this.error = error
    this.errorCode = code
  }

  async checkAuth() {
    // Check if we have a token in localStorage
    const token = localStorage.getItem('auth_token')
    if (!token) {
      this.setUser(null)
      this.isAuthenticated = false
      return false
    }

    try {
      this.setLoading(true)
      this.setError(null)

      const response = await axios.get(`${process.env.VITE_API_URL}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      runInAction(() => {
        this.setUser(response.data.user)
        this.isAuthenticated = !!response.data.user
      })

      return true
    } catch (error) {
      runInAction(() => {
        if (axios.isAxiosError(error) && error.response?.status !== 200) {
          // Token is invalid or expired
          localStorage.removeItem('auth_token')
          this.setUser(null)
          this.isAuthenticated = false
          this.setError('Authentication expired')
        } else {
          this.setError('Failed to authenticate')
          console.error('Authentication check failed:', error)
        }
      })

      return false
    } finally {
      runInAction(() => {
        this.setLoading(false)
      })
    }
  }

  /** Set by the last sign-in when it created the account, for redeemJoinToken's log. */
  createdAccount: boolean = false

  async loginWithGoogle(googleToken: string, joinToken?: string) {
    try {
      this.setLoading(true)
      this.setError(null)

      const response = await axios.post(`${process.env.VITE_API_URL}/auth/google`, {
        token: googleToken,
        joinToken,
      })

      runInAction(() => {
        // Store the token returned from the backend
        localStorage.setItem('auth_token', response.data.token)
        this.setUser(response.data.user)
        this.isAuthenticated = true
        this.createdAccount = response.data.createdAccount === true
      })

      return response.data.user
    } catch (error) {
      runInAction(() => {
        // The server explains a refused join link ("no places left"), which a generic
        // message would swallow on exactly the path the user needs it.
        if (axios.isAxiosError(error) && error.response?.data?.error) {
          this.setError(error.response.data.error, error.response.data.code ?? null)
        } else {
          this.setError('Failed to login with Google')
        }
        console.error('Google login failed:', error)
      })
      throw error
    } finally {
      runInAction(() => {
        this.setLoading(false)
      })
    }
  }

  async loginWithMicrosoft(microsoftToken: string, joinToken?: string) {
    try {
      this.setLoading(true)
      this.setError(null)

      const response = await axios.post(`${process.env.VITE_API_URL}/auth/microsoft`, {
        token: microsoftToken,
        joinToken,
      })

      runInAction(() => {
        // Store the token returned from the backend
        localStorage.setItem('auth_token', response.data.token)
        this.setUser(response.data.user)
        this.isAuthenticated = !!response.data.user
        this.createdAccount = response.data.createdAccount === true
      })

      return response.data.user
    } catch (error) {
      runInAction(() => {
        if (axios.isAxiosError(error) && error.response?.data?.error) {
          this.setError(error.response.data.error, error.response.data.code ?? null)
        } else {
          this.setError('Failed to login with Microsoft')
        }
        console.error('Microsoft login failed:', error)
      })
      throw error
    } finally {
      runInAction(() => {
        this.setLoading(false)
      })
    }
  }

  async loginWithEmailCode(email: string, code: string, joinToken?: string) {
    try {
      this.setLoading(true)
      this.setError(null)

      const response = await axios.post(`${process.env.VITE_API_URL}/auth/email/verify-code`, {
        email,
        code,
        joinToken,
      })

      runInAction(() => {
        localStorage.setItem('auth_token', response.data.token)
        this.setUser(response.data.user)
        this.isAuthenticated = true
        this.createdAccount = response.data.createdAccount === true
      })

      return response.data.user
    } catch (error) {
      runInAction(() => {
        if (axios.isAxiosError(error) && error.response?.data?.error) {
          this.setError(error.response.data.error, error.response.data.code ?? null)
        } else {
          this.setError('Failed to verify code')
        }
        console.error('Email code login failed:', error)
      })
      throw error
    } finally {
      runInAction(() => {
        this.setLoading(false)
      })
    }
  }

  // Alias for backward compatibility
  async login(googleToken: string) {
    return this.loginWithGoogle(googleToken)
  }

  logout() {
    localStorage.removeItem('auth_token')
    this.setUser(null)
    this.isAuthenticated = false
    // Navigation to auth page will be handled by the component using react-router
  }
}

export const userStore = new UserStore()
